import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a paginated blog posts list
 */
export default class BlogPostsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();
    this.initBlogSearch();
  }

  initBlogSearch() {
    const grid = this.refs.grid || this.querySelector('.blog-posts-container');
    if (!grid) return;

    const getSearchElements = () => {
      return {
        searchInput: document.querySelector('#BlogSearchInput'),
        searchButton: document.querySelector('#BlogSearchSubmit'),
        clearButton: document.querySelector('#BlogSearchClear'),
        noResultsMessage: document.querySelector('#BlogSearchNoResults'),
        paginationContainer: document.querySelector('.blog-pagination-container')
      };
    };

    const performSearch = (rawQuery) => {
      const query = (rawQuery || '').trim().toLowerCase();

      // Check if we are on a tagged page, and if so, redirect to the main blog page with the query
      const taggedIndex = window.location.pathname.indexOf('/tagged/');
      if (taggedIndex !== -1 && query) {
        const blogUrl = window.location.pathname.substring(0, taggedIndex);
        window.location.href = `${blogUrl}?q=${encodeURIComponent(query)}`;
        return;
      }

      let matchCount = 0;

      const {
        clearButton,
        noResultsMessage,
        paginationContainer
      } = getSearchElements();
      const currentItems = Array.from(grid.querySelectorAll('.blog-post-item'));

      currentItems.forEach(item => {
        const searchIndex = item.getAttribute('data-search-index') || item.textContent || '';
        const isMatch = searchIndex.toLowerCase().includes(query);

        if (query) {
          if (isMatch) {
            item.style.display = '';
            matchCount++;
          } else {
            item.style.display = 'none';
          }
        } else {
          if (item.getAttribute('data-extra-item') === 'true') {
            item.style.display = 'none';
          } else {
            item.style.display = '';
          }
        }
      });

      // Update URL query parameter
      const url = new URL(window.location.href);
      if (query) {
        url.searchParams.set('q', query);
        if (clearButton) clearButton.style.display = 'flex';
        if (paginationContainer) paginationContainer.style.display = 'none';
      } else {
        url.searchParams.delete('q');
        if (clearButton) clearButton.style.display = 'none';
        if (paginationContainer) paginationContainer.style.display = '';
      }
      history.replaceState({}, '', url.toString());

      // Update page title
      const h1 = this.querySelector('h1');
      if (h1) {
        const originalTitle = h1.getAttribute('data-original-title') || h1.textContent;
        if (query) {
          h1.textContent = `Search results for: ${rawQuery.trim()}`;
        } else {
          h1.textContent = originalTitle;
        }
      }

      // Show/hide no results message
      if (noResultsMessage) {
        if (query && matchCount === 0) {
          noResultsMessage.style.display = 'block';
        } else {
          noResultsMessage.style.display = 'none';
        }
      }
    };

    // Use document event delegation to make sure it handles dynamic elements
    document.addEventListener('click', (e) => {
      const submitBtn = e.target.closest('#BlogSearchSubmit');
      const clearBtn = e.target.closest('#BlogSearchClear');

      if (submitBtn) {
        const { searchInput } = getSearchElements();
        if (searchInput) performSearch(searchInput.value);
      } else if (clearBtn) {
        const { searchInput } = getSearchElements();
        if (searchInput) {
          searchInput.value = '';
          performSearch('');
        }
      }
    });

    document.addEventListener('keydown', (e) => {
      const input = e.target.closest('#BlogSearchInput');
      if (input && e.key === 'Enter') {
        e.preventDefault();
        performSearch(input.value);
      }
    });

    // Determine current page from URL
    const urlParams = new URLSearchParams(window.location.search);
    const currentPage = parseInt(urlParams.get('page') || '1', 10);

    // Find all pages from the pagination links
    const getPageNumbers = () => {
      const pages = new Set([currentPage]);
      document.querySelectorAll('.blog-pagination-container a.page, .blog-pagination-container span.page').forEach(el => {
        const pageNum = parseInt(el.textContent.trim(), 10);
        if (!isNaN(pageNum)) {
          pages.add(pageNum);
        }
      });
      document.querySelectorAll('.blog-pagination-container a').forEach(el => {
        const href = el.getAttribute('href');
        if (href) {
          const match = href.match(/[?&]page=(\d+)/);
          if (match) {
            pages.add(parseInt(match[1], 10));
          }
        }
      });
      return Array.from(pages).sort((a, b) => a - b);
    };

    const allPages = getPageNumbers();

    // Fetch all other pages asynchronously in the background
    allPages.forEach(p => {
      if (p === currentPage) return;

      const pageUrl = new URL(window.location.href);
      pageUrl.searchParams.set('page', p.toString());

      fetch(pageUrl.toString())
        .then(response => response.text())
        .then(html => {
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');
          const extraItems = doc.querySelectorAll('.blog-post-item');

          extraItems.forEach(item => {
            item.setAttribute('data-extra-item', 'true');
            const currentQuery = (document.querySelector('#BlogSearchInput')?.value || '').trim();
            if (!currentQuery) {
              item.style.display = 'none';
            }
            grid.appendChild(item);
          });

          // Re-run search if a query was active while fetching
          const currentQuery = (document.querySelector('#BlogSearchInput')?.value || '').trim();
          if (currentQuery) {
            performSearch(currentQuery);
          }
        })
        .catch(err => console.error('Failed to load page ' + p, err));
    });

    // Check for query param on page load
    const initialQuery = urlParams.get('q');
    if (initialQuery) {
      let attempts = 0;
      const interval = setInterval(() => {
        attempts++;
        const { searchInput } = getSearchElements();
        if (searchInput) {
          searchInput.value = initialQuery;
          performSearch(initialQuery);
          clearInterval(interval);
        } else if (attempts > 30) {
          clearInterval(interval);
        }
      }, 100);
    }
  }
}

if (!customElements.get('blog-posts-list')) {
  customElements.define('blog-posts-list', BlogPostsList);
}
